// src/pages/attributes.tsx

/**
 * Attributes Page
 * Main page for managing attribute definitions
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw, Moon, Sun, AlertCircle, Plus, Search } from 'lucide-react'
import { toast } from 'sonner'

import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AttributesTable,
  AttributeFormPanel,
  DeleteAttributeDialog,
  AttributesEmpty,
} from '@/components/attributes'
import {
  useAttributes,
  useCreateAttribute,
  useUpdateAttribute,
  useDeleteAttribute,
} from '@/hooks/use-attributes'
import { useThemeStore } from '@/stores/theme-store'
import type {
  Attribute,
  AttributeCategory,
  CreateAttributeRequest,
  UpdateAttributeRequest,
} from '@/types/attribute'
import { CATEGORY_LABELS } from '@/types/attribute'
import { cn } from '@/lib/utils'

const CATEGORIES: AttributeCategory[] = ['SUBJECT', 'RESOURCE', 'ACTION', 'ENVIRONMENT']
const DEFAULT_PAGE_SIZE = 10

export function AttributesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme, toggleTheme } = useThemeStore()

  // Get category from URL or default to SUBJECT
  const categoryParam = searchParams.get('category')?.toUpperCase() as AttributeCategory | null
  const activeCategory: AttributeCategory =
    categoryParam && CATEGORIES.includes(categoryParam) ? categoryParam : 'SUBJECT'

  // Local state
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [formOpen, setFormOpen] = useState(false)
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null)
  const [deletingAttribute, setDeletingAttribute] = useState<Attribute | null>(null)

  // Fetch attributes for active category
  const { data, isLoading, isError, error, refetch } = useAttributes({
    category: activeCategory,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: searchQuery || undefined,
  })

  // Mutations
  const createMutation = useCreateAttribute()
  const updateMutation = useUpdateAttribute()
  const deleteMutation = useDeleteAttribute()

  // Handle tab change
  const handleTabChange = useCallback((value: string) => {
    setSearchParams({ category: value.toLowerCase() })
    setPage(1)
    setSearchQuery('')
  }, [setSearchParams])

  // Handle search
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setPage(1)
  }, [])

  // Open form for new attribute
  const handleNewAttribute = useCallback(() => {
    setEditingAttribute(null)
    setFormOpen(true)
  }, [])

  // Open form for editing
  const handleEdit = useCallback((attribute: Attribute) => {
    setEditingAttribute(attribute)
    setFormOpen(true)
  }, [])

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (formData: CreateAttributeRequest | UpdateAttributeRequest) => {
      if (editingAttribute) {
        await updateMutation.mutateAsync({
          id: editingAttribute.id,
          data: formData as UpdateAttributeRequest,
        })
      } else {
        await createMutation.mutateAsync(formData as CreateAttributeRequest)
      }
      setFormOpen(false)
      setEditingAttribute(null)
    },
    [editingAttribute, createMutation, updateMutation]
  )

  // Handle delete
  const handleDelete = useCallback((attribute: Attribute) => {
    setDeletingAttribute(attribute)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingAttribute) return

    try {
      await deleteMutation.mutateAsync(deletingAttribute.id)
      toast.success(`Attribute "${deletingAttribute.displayName}" deleted`)
      setDeletingAttribute(null)
    } catch {
      // Error handled by mutation
    }
  }, [deletingAttribute, deleteMutation])

  // Debug logging in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[Attributes] Query result:', {
        category: activeCategory,
        isLoading,
        isError,
        error,
        data,
        dataType: typeof data,
        hasDataProperty: data && 'data' in data,
        dataDataType: data && 'data' in data ? typeof data.data : 'n/a',
      })
    }
  }, [activeCategory, isLoading, isError, error, data])

  // Get attributes from response data
  // The API returns { data: Attribute[], pagination: {...} }
  const attributes = useMemo(() => {
    // Handle both wrapped response format and direct array format
    if (!data) return []
    if (Array.isArray(data)) return data as Attribute[]
    if (data && 'data' in data && Array.isArray(data.data)) return data.data as Attribute[]
    return []
  }, [data])

  const pagination = data && !Array.isArray(data) ? data.pagination : undefined

  // Keep page in range when filters/deletes shrink total pages
  useEffect(() => {
    if (!pagination) return
    const totalPages = Math.max(1, pagination.totalPages || 1)
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [pagination, page])

  // Get counts for each category (shows count for active category)
  const getCategoryCount = (category: AttributeCategory) => {
    if (category === activeCategory) {
      return pagination?.totalItems ?? attributes.length
    }
    return null // Don't show count for other tabs
  }

  const showPagination =
    !!pagination &&
    (pagination.totalPages > 1 || pagination.totalItems > DEFAULT_PAGE_SIZE)

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Attributes</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleNewAttribute} className="h-8">
            <Plus className="mr-2 h-3.5 w-3.5" />
            New Attribute
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw
              className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Page Description */}
        <div>
          <p className="text-sm text-muted-foreground">
            Define attributes for subjects, resources, actions, and environment to use in policies
          </p>
        </div>

        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load attributes:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="ml-4"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={handleTabChange}>
          <div className="flex items-center justify-between gap-4">
            <TabsList>
              {CATEGORIES.map((category) => {
                const count = getCategoryCount(category)
                return (
                  <TabsTrigger key={category} value={category}>
                    {CATEGORY_LABELS[category]}
                    {count !== null && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        ({count})
                      </span>
                    )}
                  </TabsTrigger>
                )
              })}
            </TabsList>

            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search attributes..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-8 h-9"
              />
            </div>
          </div>

          {/* Tab Content */}
          {CATEGORIES.map((category) => (
            <TabsContent key={category} value={category} className="mt-4">
              {!isLoading && attributes.length === 0 && !searchQuery ? (
                <AttributesEmpty
                  category={category}
                  onCreateClick={handleNewAttribute}
                />
              ) : (
                <>
                  <AttributesTable
                    attributes={attributes}
                    isLoading={isLoading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />

                  {showPagination && pagination && (
                    <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                      <span>
                        Page {pagination.page} of{' '}
                        {Math.max(1, pagination.totalPages)} ({pagination.totalItems}{' '}
                        total)
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            page <= 1 ||
                            isLoading ||
                            pagination.hasPreviousPage === false
                          }
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            isLoading ||
                            page >= Math.max(1, pagination.totalPages) ||
                            pagination.hasNextPage === false
                          }
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Form Panel */}
      <AttributeFormPanel
        open={formOpen}
        onOpenChange={setFormOpen}
        attribute={editingAttribute}
        category={activeCategory}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Delete Dialog */}
      <DeleteAttributeDialog
        open={!!deletingAttribute}
        onOpenChange={(open) => !open && setDeletingAttribute(null)}
        attributeKey={deletingAttribute?.key ?? ''}
        attributeName={deletingAttribute?.displayName ?? ''}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </>
  )
}

export default AttributesPage
